import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_ABS_STRUCTURE, absScreenBody, screenCached, deskAbsStructure } from './absCache.js';
import {
  ABS_NOTE_UNSCALED_LABEL,
  BINDING_LABEL,
  PRICE_BANDS,
  ABS_BASE_STRIP,
  deckPricePhrase,
  fmtMM,
  noteCasePointer,
  stripInputsFromAbsRun,
  fmtMonth,
  fmtX,
  gapLabel,
  packageFacts,
  pvReconciliation,
  stressCaseCopy,
} from './model.js';
import { Loading, NextStep, Src, useAssistantSummary, useMeeting, useServerScreen } from './common.jsx';

const DEFAULT_STRUCTURE = DEFAULT_ABS_STRUCTURE;

const FIELDS = [
  { key: 'advPv10', label: 'Advance on PV10', pct: true, bench: 'Bank RBL 55–60% · ABS ~65–75% all-in', src: 'model' },
  { key: 'advPp', label: 'Advance on purchase price', pct: true, bench: 'confirm with placement agent', src: 'model' },
  { key: 'targetDscr', label: 'Every month covers debt service', x: true, bench: 'weakest month sets the note', src: 'model' },
  { key: 'coupon', label: 'Coupon', pct: true, bench: 'placeholder, not a quote', src: 'judgment' },
  { key: 'tenorYears', label: 'Tenor (years)', bench: '—', src: 'judgment' },
];

const finite = (v) => typeof v === 'number' && Number.isFinite(v);

function bridgeLeg(label, contribution) {
  const n = Number(contribution);
  if (!Number.isFinite(n)) return `+ ${label} —`;
  const op = n < 0 ? '−' : '+';
  return `${op} ${label} ${fmtMM(Math.abs(n), 2)}`;
}

export default function Finance({ dealId, dealPackage, dealPackageFailed, executive, reserves, onNavigate }) {
  const { desk, patchDesk } = useMeeting({ dealId, dealPackage });
  const serverScreen = useServerScreen(dealId);
  // Applied structure is persisted on the desk so Package's capital tape matches this note.
  const [structure, setStructureState] = useState(() => deskAbsStructure(desk));
  const [draft, setDraft] = useState(() => toDraft(deskAbsStructure(desk)));
  const setStructure = (next) => {
    setStructureState(next);
    patchDesk({ absStructure: next === DEFAULT_STRUCTURE ? null : { ...next } });
  };
  const [screen, setScreen] = useState(null);
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const gen = useRef(0);
  const facts = useMemo(
    () => (dealPackage ? packageFacts(dealPackage, { executive, reserves }) : null),
    [dealPackage, executive, reserves],
  );

  const run = useCallback(
    async (struct) => {
      const g = ++gen.current;
      setRunning(true);
      setError(null);
      try {
        const result = await screenCached(absScreenBody(dealId, struct, desk));
        if (g !== gen.current) return;
        setScreen(result);
      } catch (e) {
        if (g !== gen.current) return;
        setError(e.message || String(e));
      } finally {
        if (g === gen.current) setRunning(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dealId, desk.aroMM, desk.aroSet, desk.bidPriceMM],
  );

  useEffect(() => {
    if (!dealPackage) return undefined;
    run(structure);
    // rerun when the deal, bid or ARO (value or set-state) changes; structure edits rerun via the button
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run, dealPackage]);

  const base = screen?.base || null;
  const head = base?.summary?.headline || base?.solve || null;
  const down = screen?.downside?.summary?.headline || null;
  const cov = base?.coverage || null;
  const bid = desk.bidPriceMM;
  const note = head?.maxNoteSizeMM ?? null;
  const baseCaseNote = (serverScreen?.result?.criteria || []).find((c) => c.key === 'absNote')?.value ?? null;
  const strip = stripInputsFromAbsRun(base);
  const stripPhrase = deckPricePhrase(strip);
  const baseCasePointer = noteCasePointer('finance', baseCaseNote, { strip });
  const equity = finite(note) && finite(bid) ? Math.max(0, bid - note) : null;
  const debtPct = finite(note) && bid > 0 ? Math.min(1, note / bid) : 0;
  const gaps = Array.from(new Set([...(base?.screenGaps || []), ...(screen?.exportEligibility?.reasons || [])]));
  const hedges = base?.hedges || [];
  const executed = hedges.filter((h) => h.status === 'executed').length;
  const rblLo = finite(facts?.refPdpPv10MM) ? facts.refPdpPv10MM * 0.55 : null;
  const rblHi = finite(facts?.refPdpPv10MM) ? facts.refPdpPv10MM * 0.6 : null;
  const aroMissing = !desk.aroSet;
  const reconciliation = useMemo(() => {
    if (!dealPackage) return null;
    // The screen prices the base preset's strip, not the package deck.
    // Use those run prices. Fall back to the shipped strip only before they arrive.
    return pvReconciliation(dealPackage, {
      collateralPvMM: head?.collateralPvMM ?? null,
      aroMM: head?.aroUnknown ? 0 : desk.aroMM || 0,
      absWti: finite(strip.wti) ? strip.wti : ABS_BASE_STRIP.wti,
      absHh: finite(strip.hh) ? strip.hh : ABS_BASE_STRIP.hh,
    });
  }, [dealPackage, head, desk.aroMM, strip.wti, strip.hh]);

  useAssistantSummary(
    head
      ? {
          surface: 'finance',
          bidPriceMM: bid,
          bidEdited: desk.bidEdited === true,
          noteLabel: ABS_NOTE_UNSCALED_LABEL,
          noteCase: stripPhrase ? `base strip, ${stripPhrase}` : 'base strip',
          baseStrip: strip,
          maxNoteSizeMM: note,
          financeNoteMM: note,
          analysisNoteMM: baseCaseNote,
          baseCaseNotePointer: baseCasePointer,
          bindingConstraint: head.bindingConstraint,
          illustrativeMaxPurchasePriceMM: head.illustrativeMaxPurchasePriceMM,
          minDscr: head.minDscr,
          collateralPvMM: head.collateralPvMM,
        }
      : null,
  );

  if (!dealPackage) {
    return dealPackageFailed ? (
      <div className="mp">
        <div className="mp-empty">No deal package is loaded for this deal on this host yet.</div>
      </div>
    ) : (
      <Loading label="Loading the package…" />
    );
  }

  return (
    <div className="mp">
      <div className="mp-head">
        <div>
          <span className="mp-eyebrow">
            Finance · desk only, investors never see this step · sponsor assumptions, not lender quotes
          </span>
          <h2 className="mp-h2">How much of the purchase can debt carry?</h2>
          {dealPackage?.productionHistory?.grain === 'lease' ? (
            <p className="mp-note">
              {dealPackage.productionHistory.leases.length} RRC{' '}
              {dealPackage.productionHistory.leases.length === 1 ? 'lease' : 'leases'} with history
              {dealPackage.productionHistory.leases.some((l) => l.forecast?.length)
                ? ", shown against the seller's forecast case."
                : '.'}{' '}
              Source: {dealPackage.productionHistory.source || 'lease production'}.
            </p>
          ) : null}
        </div>
        <button type="button" className="mp-link" onClick={() => onNavigate('abs')}>
          Full ABS desk →
        </button>
      </div>

      {aroMissing ? (
        <div className="mp-card" style={{ borderColor: 'var(--mp-amber-line)', background: 'var(--mp-amber-bg)' }}>
          <p className="mp-p">
            <b>ARO isn't set.</b> Lenders won't size a note until plug &amp; abandonment is funded or
            explicitly underwritten. Set it on Value.{' '}
            <button type="button" className="mp-link" onClick={() => onNavigate('value')}>
              Set ARO on Value →
            </button>
          </p>
        </div>
      ) : null}

      {error ? (
        <div className="mp-empty" role="alert">
          The ABS engine couldn't run for this deal: {error}
        </div>
      ) : null}

      {!screen && running ? <Loading label="Sizing the note…" /> : null}

      {head ? (
        <>
          <section className="mp-card" aria-label="Capital stack" style={{ opacity: running ? 0.6 : 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
              <h3 className="mp-h3">Capital stack at a {fmtMM(bid, 1)} purchase</h3>
              <span className="mp-note">before transaction fees</span>
            </div>
            <div className="mp-stack" aria-hidden="true">
              {debtPct > 0 ? (
                <div className="debt" style={{ width: `${Math.max(debtPct * 100, 14)}%` }}>
                  ABS note {fmtMM(note, 2)}
                </div>
              ) : null}
              <div className="equity" style={{ flexGrow: 1 }}>
                Equity needed ≈ {fmtMM(equity, 2)}
              </div>
            </div>
            <div className="mp-tiles">
              <div className="mp-tile">
                <span className="k">{ABS_NOTE_UNSCALED_LABEL}</span>
                <span className="v">{fmtMM(note, 2)}</span>
              </div>
              <div className={`mp-tile ${head.bindingConstraint ? 'bad' : ''}`}>
                <span className="k">What limits it</span>
                <span className="v" style={{ fontFamily: 'inherit', fontSize: 18 }}>
                  {BINDING_LABEL[head.bindingConstraint] || head.bindingConstraint || '—'}
                </span>
                {cov?.bindingMonth ? (
                  <span className="s">
                    every month covers debt service {fmtX(head.targetDscr)} · tightest {fmtMonth(cov.bindingMonth)}
                  </span>
                ) : (
                  <span className="s">every month covers debt service {fmtX(head.targetDscr)}</span>
                )}
              </div>
              <div className="mp-tile">
                <span className="k">Collateral PV (after ARO)</span>
                <span className="v">{fmtMM(head.collateralPvMM, 2)}</span>
                <span className="s">1PDP on the base strip, after ARO</span>
              </div>
              <div className="mp-tile">
                <span className="k">Year-1 cash flow</span>
                <span className="v">{fmtMM(head.year1CfMM, 2)}</span>
              </div>
              <div className="mp-tile">
                <span className="k">Bank RBL benchmark</span>
                <span className="v" style={{ fontSize: 17 }}>
                  {finite(rblLo) ? `${fmtMM(rblLo, 1)}–${fmtMM(rblHi, 1)}` : '—'}
                </span>
                <span className="s">55–60% of seller PDP PV10</span>
              </div>
            </div>
            <p className="mp-p">
              At these terms the note supports a purchase of about{' '}
              <b>{fmtMM(head.illustrativeMaxPurchasePriceMM, 2)}</b>.{' '}
              {finite(head.illustrativeMaxPurchasePriceMM) && bid > head.illustrativeMaxPurchasePriceMM
                ? `To close at ${fmtMM(bid, 1)} the rest is equity, unless coverage improves (lower coupon, longer tenor, or hedges that are actually executed).`
                : 'The bid sits within what the note supports at these terms.'}{' '}
              <span className="mp-note" data-testid="note-case-pointer">
                {baseCasePointer}
              </span>
            </p>
            <p className="mp-p" data-testid="pv-reconciliation">
              {reconciliation ? (
                <>
                  <b>PV reconciliation.</b> Desk PV10 {fmtMM(reconciliation.deskPv10MM, 2)}
                  {' '}{bridgeLeg('calibration', -reconciliation.calibrationScaleMM)}
                  {' '}{bridgeLeg('non-PDP', -reconciliation.nonPdpMM)}
                  {' '}{bridgeLeg('ARO', -reconciliation.aroMM)}
                  {' '}{bridgeLeg('price deck', reconciliation.priceDeckMM)}
                  {' = '}{fmtMM(reconciliation.impliedCollateralMM, 2)}
                  . Collateral PV-10 is {fmtMM(reconciliation.collateralPvMM, 2)}
                  {reconciliation.residualMM == null
                    ? '.'
                    : `. Residual ${fmtMM(reconciliation.residualMM, 2)}.`}
                  {' '}Each figure is computed for this deal. ABS keeps the conservative base strip at WTI $
                  {reconciliation.absWti.toFixed(2)} and HH ${reconciliation.absHh.toFixed(2)}. The desk deck is WTI $
                  {finite(reconciliation.deskWti) ? reconciliation.deskWti.toFixed(2) : '—'}
                  {reconciliation.gasPriced
                    ? ` and HH $${finite(reconciliation.deskHh) ? reconciliation.deskHh.toFixed(2) : '—'}`
                    : ' (oil-only; the gas price is not copied from WTI)'}.
                  {' '}{PRICE_BANDS.abs}. {stressCaseCopy(dealPackage)}
                </>
              ) : null}
            </p>
          </section>

          <div className="mp-row">
            <section className="mp-card mp-grow-3" aria-label="Structure">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                <h3 className="mp-h3">Structure: ours vs. what the market quotes</h3>
                <span className="mp-note">edit, then recalculate</span>
              </div>
              <div className="mp-table-wrap">
                <table className="mp-table" style={{ minWidth: 560 }}>
                  <thead>
                    <tr>
                      <th>Term</th>
                      <th>Our assumption</th>
                      <th>Market benchmark</th>
                      <th>Basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {FIELDS.map((f) => (
                      <tr key={f.key}>
                        <td>{f.label}</td>
                        <td style={{ paddingTop: 6, paddingBottom: 6 }}>
                          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input
                              className="mp-param-input"
                              style={{ width: 90 }}
                              aria-label={f.label}
                              inputMode="decimal"
                              value={draft[f.key]}
                              onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                            />
                            <span className="mp-note">{f.pct ? '%' : f.x ? '×' : ''}</span>
                          </label>
                        </td>
                        <td className="mp-note" style={{ color: 'var(--mp-ink-2)' }}>
                          {f.bench}
                        </td>
                        <td>
                          <Src kind={f.src} />
                        </td>
                      </tr>
                    ))}
                    <tr>
                      <td>Hedges</td>
                      <td className="mp-num">
                        {hedges.length ? `${Math.round((hedges[0].notionalPctOfPdp || 0) * 100)}% of PDP` : '—'}
                      </td>
                      <td>
                        {executed} executed · {hedges.length - executed} proposed
                      </td>
                      <td>
                        <Src kind="judgment" />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  type="button"
                  className="mp-btn"
                  disabled={running}
                  onClick={() => {
                    const next = fromDraft(draft, structure);
                    setStructure(next);
                    setDraft(toDraft(next));
                    run(next);
                  }}
                >
                  {running ? 'Calculating…' : 'Recalculate'}
                </button>
                <button
                  type="button"
                  className="mp-btn ghost"
                  disabled={running}
                  onClick={() => {
                    setStructure(DEFAULT_STRUCTURE);
                    setDraft(toDraft(DEFAULT_STRUCTURE));
                    run(DEFAULT_STRUCTURE);
                  }}
                >
                  Reset
                </button>
              </div>
            </section>

            <div className="mp-grow-2" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <section className="mp-card" aria-label="Downside case">
                <h3 className="mp-h3">Downside case</h3>
                <p className="mp-p">
                  {down
                    ? down.maxNoteSizeMM > 0
                      ? `On the internal planning downside the note sizes to ${fmtMM(down.maxNoteSizeMM, 2)} (minimum coverage ${fmtX(down.minDscr)}).`
                      : `On the internal planning downside the note sizes to $0: ${
                          down.bindingConstraint === 'negativeCf'
                            ? 'cash flow turns negative'
                            : 'coverage never reaches the minimum'
                        }. Lenders will run a case like this, so the structure depends on the forecast holding up.`
                    : 'Downside not run.'}
                </p>
                <div>
                  <button type="button" className="mp-link" onClick={() => onNavigate('forecast')}>
                    Revisit the forecast →
                  </button>
                </div>
              </section>
              <section className="mp-card" aria-label="Before a lender sees it">
                <h3 className="mp-h3">Before a lender sees it</h3>
                {gaps.length ? (
                  gaps.slice(0, 7).map((g) => (
                    <div key={g} className="mp-check">
                      <span className="no">○</span>
                      <span>{gapLabel(g)}</span>
                    </div>
                  ))
                ) : (
                  <div className="mp-check">
                    <span className="ok">✓</span>
                    <span>No open tape gaps.</span>
                  </div>
                )}
                {!aroMissing ? (
                  <div className="mp-check">
                    <span className="ok">✓</span>
                    <span>ARO funded ({fmtMM(desk.aroMM, 2)}) and deducted from collateral PV</span>
                  </div>
                ) : null}
              </section>
            </div>
          </div>

          <div className="mp-row" style={{ alignItems: 'center' }}>
            <span className="mp-note mp-grow-3">
              Not a financing commitment, rating or offer. Terms are sponsor assumptions until a named
              counterparty confirms them. Advance-rate benchmarks are market reads, not quotes.
            </span>
            <div className="mp-grow-2">
              <NextStep label="5 · Package" question="Freeze a case and send what each audience needs." onClick={() => onNavigate('package')} />
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

function toDraft(s) {
  return {
    advPv10: String(+(s.advPv10 * 100).toFixed(1)),
    advPp: String(+(s.advPp * 100).toFixed(1)),
    targetDscr: String(s.targetDscr),
    coupon: String(+(s.coupon * 100).toFixed(2)),
    tenorYears: String(s.tenorYears),
  };
}

function fromDraft(d, prev) {
  const num = (v, fallback) => {
    const n = Number(String(v).replace(/[%×x,\s]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : fallback;
  };
  return {
    ...prev,
    advPv10: num(d.advPv10, prev.advPv10 * 100) / 100,
    advPp: num(d.advPp, prev.advPp * 100) / 100,
    targetDscr: num(d.targetDscr, prev.targetDscr),
    coupon: num(d.coupon, prev.coupon * 100) / 100,
    tenorYears: num(d.tenorYears, prev.tenorYears),
  };
}

